/**
 * SF-179: shared unit-domain deficit/surplus math — the units-domain analog of
 * `components/buildings/buildingsPercent.ts`. Single source of truth for Train's per-unit deficit
 * (mobile row, desktop `TrainTable`, bulk/single-row Fill Strategy) and Release's per-unit surplus
 * (bulk/per-row Release to Strategy).
 *
 * Named `unitStockMath.ts`, not `unitsPercent.ts` (Phase 3 Decision, resolves Phase 2 frontend-dev
 * C10) — no percent math is involved here, only absolute unit counts. `unitsPercent.ts`/`PercentRow`
 * would invite a false parallel CLAUDE.md already documents as rejected for Training specifically
 * ("uncapped per-acre ratios can exceed 100%").
 */

export interface ComputeUnitDeficitParams {
  target: number;
  owned: number;
  inProgress: number;
}

export interface ComputeUnitDeficitResult {
  deficit: number;
}

/**
 * Mirrors `buildingsPercent.ts`'s `computeDeficit`: `current = owned + inProgress`,
 * `deficit = max(0, target - current)`. `owned`/`inProgress`/`target` are always whole units
 * server-side (per-batch `unitCount` is integer, and `getTarget()` already applies `Math.floor`
 * once when computing the target from `armyComp × totalLand`) — no additional rounding needed here.
 *
 * Deliberately has no `atTarget` field (Phase 3 Decision) — both `atTarget` at `target === 0` was
 * degenerate and wrong (mirrors `buildingsPercent.ts`'s own SF-165 precedent for dropping exactly
 * this shape). Callers already have a separate `hasTarget` boolean and derive "at target" inline as
 * `hasTarget && deficit === 0`.
 */
export function computeUnitDeficit({ target, owned, inProgress }: ComputeUnitDeficitParams): ComputeUnitDeficitResult {
  const current = owned + inProgress;
  return { deficit: Math.max(0, target - current) };
}

export interface ComputeUnitSurplusParams {
  target: number | null;
  owned: number;
}

export interface ComputeUnitSurplusResult {
  surplus: number;
}

/**
 * Mirrors `buildingsPercent.ts`'s `computeRazeSurplus` exactly, including its `null`-target
 * short-circuit (Phase 3 Decision 1): `target === null` (no active strategy at all) → surplus 0,
 * nothing pre-filled; `target === 0` from an *active* strategy that explicitly sets this unit
 * type's target to 0 is a real target, and surplus resolves to the full owned count — the same
 * "explicit 0 means release/raze to zero" semantic `computeRazeSurplus` already uses for buildings.
 */
export function computeUnitSurplus({ target, owned }: ComputeUnitSurplusParams): ComputeUnitSurplusResult {
  if (target === null) return { surplus: 0 };
  return { surplus: Math.max(0, owned - target) };
}
